<template>
  <interview-layout :touch-and-validate="touchAndValidate">
    <div style="max-width: 640px;">
      <h1 class="px-4 text-primary fade-in-up-enter-active animation-slowest">Maatregelen</h1>
      <h5 class="px-4 pb-3 fade-in-up-enter-active animation-slowest delay-200">Wat kan er verbeterd worden?</h5>

      <base-card class="w-100" inner-class="vw-100" color="alternate" :outer-padding="true">
        <base-form @submit="onSubmit">
          <button-grid-input
            name="Interests"
            btn-direction="row"
            type="checkbox"
            btn-class="min-40"
            :pairs-mode="true"
            :distribute-equally="true"
            :options="interestOptions"
            variant="minimal-filled-info"
            inactiveVariant="minimal-dark"
            v-model:value="lead.interests.Interests"
            @update:value="updateInterests"
          >
            Kies de maatregelen waarin u interesse heeft:
          </button-grid-input>

          <!-- Add description for maatregel -->
          <base-card
            color="light"
            class="mt-4 interests-notes-card w-100"
            inner-class="p-3 w-100"
          >
          <h6 class="mb-3 text-primary">Algemene toelichting</h6>
            <base-input
              type="textarea"
              name="algemene-toelichting"
              id="algemene-toelichting"
              v-model:value="lead.interests.InterestNotes['Algemene_toelichting']"
              :rows="3"
              :min-rows="3"
              :autosize="true"
              :input-class="'form-control w-100 border-color-secondary'"
              :placeholder="'Geef hier een korte toelichting'"
            />
            <div v-if="lead.interests.Interests && lead.interests.Interests.length > 0">

              <h6 class="mb-3 text-primary">Toelichting per maatregel</h6>
              <div
                v-for="interest in lead.interests.Interests"
                :key="interest"
                class="mb-2"
              >
                <label :for="'note-' + normalizeKey(interest)" class="fw-500 mb-2 text-secondary d-block">
                  <span v-html="getInterestLabel(interest)"></span>
                </label>
                <base-input
                  type="textarea"
                  :name="'note-' + normalizeKey(interest)"
                  :id="'note-' + normalizeKey(interest)"
                  v-model:value="lead.interests.InterestNotes[normalizeKey(interest)]"
                  :rows="3"
                  :min-rows="3"
                  :autosize="true"
                  :input-class="'form-control w-100 border-color-secondary'"
                  :placeholder="'Geef hier een korte toelichting'"
                />
              </div>
            </div>
          </base-card>

          <button-row class="mt-5">
            <base-button class="ms-auto" icon="bi-arrow-right" type="submit" :disabled="v$.interests.$invalid">Volgende</base-button>
          </button-row>
        </base-form>
      </base-card>

      <button-row class="mt-2">
        <base-button variant="minimal-secondary" icon="bi-arrow-left" icon-position="start" @click="onPrev">Terug</base-button>
      </button-row>
    </div>
  </interview-layout>
</template>

<script>
  import InterviewLayout from "@/views/layouts/InterviewLayout.vue";
  import BaseCard from "@/components/BaseCard.vue";
  import BaseForm from "@/components/BaseForm.vue";
  import BaseButton from "@/components/BaseButton.vue";
  import ButtonRow from "@/components/ButtonRow.vue";
  import ButtonGridInput from "@/components/ButtonGridInput.vue";
  import BaseInput from "@/components/BaseInput.vue";

  import { useAllStores } from "@/helpers/UseAllStores";
  import { CommonComposable } from "@/helpers/CommonComposable";
  import { FlowService } from "@/services/FlowService";
  import { getInterestDefinitions } from "@/rules/InterestDefinitions";

  export default {
    components: {
      InterviewLayout,
      BaseCard,
      BaseForm,
      BaseButton,
      ButtonRow,
      ButtonGridInput,
      BaseInput,
    },
    setup(props, context) {
      return { ...CommonComposable(props, context), ...useAllStores() };
    },
    data() {
      return {
        maxCols: 1,
        btnDirection: 'column'
      };
    },
    computed: {
      interestOptions() {
        return getInterestDefinitions(
          this.lead.meta.AllInterestsEnabled,
          this.lead.meta.InterestWhiteList
        );
      },
    },
  methods: {
      normalizeKey(key) {
        const option = this.interestOptions.find(opt => opt.value === key);
        let text = option ? option.label : key;
        text = String(text).replace(/<[^>]*>/g, '').trim();
        return text.replace(/\s+/g, '_').replace(/[^\w-]/g, '');
      },
      updateInterests(selectedValues) {
        const sortedValues = this.interestOptions
          .map((option) => option.value)
          .filter((value) => selectedValues.includes(value));

        this.lead.interests.Interests = sortedValues;

        const updatedNotes = { ...this.lead.interests.InterestNotes };
        sortedValues.forEach((value) => {
          const normalizedKey = this.normalizeKey(value);
          if (!(normalizedKey in updatedNotes)) {
            updatedNotes[normalizedKey] = "";
          }
        });
        this.lead.interests.InterestNotes = updatedNotes;
      },
      // Remove notes without maatregel and remove empty notes
      cleanInterestNotes() {
        const cleanedNotes = {};
        const existingNotes = this.lead.interests.InterestNotes

        const generalKey = this.normalizeKey('Algemene toelichting');

        if (existingNotes[generalKey]?.trim() !== "") {
          cleanedNotes[generalKey] = existingNotes[generalKey];
        }

        this.lead.interests.Interests.forEach((interest) => {
          const normalizedKey = this.normalizeKey(interest);
          if (existingNotes[normalizedKey]?.trim() !== "") {
            cleanedNotes[normalizedKey] = existingNotes[normalizedKey];
          }
        });

        this.lead.interests.InterestNotes = cleanedNotes;
      },
      getInterestLabel(value) {
        const option = this.interestOptions.find((opt) => opt.value === value);
        return option ? option.label : value;
      },
      touchAndValidate() {
        this.v$.interests.$touch();
        return !this.v$.interests.$invalid;
      },
      onSubmit() {
        if (this.touchAndValidate()) {
          this.cleanInterestNotes();
          FlowService.pushNext(this);
        }
      },
      onPrev() {
        FlowService.pushPrev(this);
      },
      breakPoints() {
        if (window.innerWidth > 550) {
          this.maxCols = null;
          this.btnDirection = 'column';
        } else {
          this.maxCols = 1;
          this.btnDirection = 'row';
        }
      },
    },
    beforeRouteUpdate() {
      this.breakPoints();
    },
    mounted() {
      this.breakPoints();
      window.addEventListener("resize", this.breakPoints);
    },
    unmounted() {
      window.removeEventListener("resize", this.breakPoints);
    },
  };
</script>